import React, { useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  BasicTable,
  Button,
  ContentPage,
  DatePicker,
  InputRadio,
  InputText,
  InputTextarea,
  ModalDefault,
  ModalForm,
  NoResults,
  Notice,
  Row,
  Select,
  SelectionBar,
  SelectorPageStatus,
  Skeleton,
  StatePill,
  Text,
  Totalizers,
  createSelectionColumn,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../hooks/useService";
import RejectHoursModal from "../RejectHoursModal";
import { competenceOfDate } from "@helpers/competencePeriod";
import { isoToDate, localIsoDate } from "@helpers/localDate";
import {
  isWithinContractAndProjectPeriod,
  rangeIntersectsContractAndProjectPeriod,
} from "../../helpers/contractPeriod";
import { DomainStatus, escapeHtml, formatBRL, formatCompetencia, formatHours } from "../ow";

const HOUR_STATUS = { WAITING: "aguardando", APPROVED: "aprovado", REJECTED: "reprovado", EMITED: "nota_emitida" };

// Pílulas do filtro de status: uma por vez, "Todos" sem filtro
const STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Aguardando", value: "WAITING" },
  { label: "Aprovado", value: "APPROVED" },
  { label: "Reprovado", value: "REJECTED" },
  { label: "Nota emitida", value: "EMITED" },
];

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const Filters = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 24px;
`;

const CompetenceField = styled.div`
  width: 220px;
`;

const PersonField = styled.div`
  width: 260px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "16px"};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Inline = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

/** Horas decimais da API (7.5) no padrão do design system ("7h30"); nunca decimal. */
const hrs = (value) => formatHours((parseFloat(value) || 0) * 60);
const days = (value) => `${(parseFloat(value) || 0).toFixed(0)} dias`;

/**
 * Aba de Horas do Projeto.
 * Compartilhado entre Admin e Employee.
 *
 * Props:
 * - projectId: number
 * - userId: number
 * - isAdmin: boolean (tabela da empresa, com as horas de todos, em vez da visão do prestador)
 * - canDecide: boolean (aprovar e reprovar na tabela da empresa; padrão = isAdmin, sem ela fica só leitura)
 * - hourMarkingModel: string (DAILY, WEEKLY, MONTHLY)
 * - hourTrackingMode: string
 * - contract: objeto contrato com start_date, renewal_date e project { start_date, end_date } (usado para validar período de apontamento)
 * - startOnWaiting: boolean (abre em "Aguardando" quando a competência tem lançamento aguardando, como a aprovação de horas)
 */
export default function ProjectHoursTab({
  projectId,
  userId,
  isAdmin,
  canDecide: canDecideProp,
  hourMarkingModel,
  hourTrackingMode,
  contract,
  startOnWaiting,
}) {
  const canDecide = canDecideProp ?? isAdmin;
  // Com dia de fechamento, um apontamento posterior ao corte já conta para a
  // competência seguinte — vale avisar antes de o prestador salvar.
  const closingDay = contract?.contract_pattern?.closing_day ?? null;
  const projectHours = useService("projectHours");
  const [hours, setHours] = useState([]);
  const [fixedSummary, setFixedSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filtros
  const currentDate = new Date();
  const [competence, setCompetence] = useState({ month: currentDate.getMonth() + 1, year: currentDate.getFullYear() });

  // Seleção em massa (Admin): { [id]: true }
  const [rowSelection, setRowSelection] = useState({});

  // Reprovação (uma ou várias) sempre com motivo, que o prestador vê na linha
  const [rejectIds, setRejectIds] = useState(null);

  // New hour modal (EMP)
  const [showNewHour, setShowNewHour] = useState(false);
  const [newHourDate, setNewHourDate] = useState("");
  const [newHourMonth, setNewHourMonth] = useState(currentDate.getMonth() + 1);
  const [newHourYear, setNewHourYear] = useState(currentDate.getFullYear());
  const [newHourTotal, setNewHourTotal] = useState("");
  const [newHourDesc, setNewHourDesc] = useState("");
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Só na primeira carga: depois o filtro é de quem usa a tela
  const autoFilterApplied = useRef(!startOnWaiting);

  const markingModel = hourMarkingModel || "DAILY";
  const trackingMode = hourTrackingMode || "HOURS_ONLY";
  const isFixedMode = trackingMode === "FIXED_WITH_HOUR_ADJUSTMENT";

  // Record type for new hour (EMP)
  const [newRecordType, setNewRecordType] = useState(
    trackingMode === "ABSENCES_ONLY" ? "ABSENCE" : "HOURS"
  );

  // Admin filters
  const [filterStatus, setFilterStatus] = useState(STATUS_DATA[0]);
  const [filterUser, setFilterUser] = useState("");
  const [availableUsers, setAvailableUsers] = useState([]);

  // Absence summary
  const [absenceDays, setAbsenceDays] = useState(0);
  const [waitingAbsenceDays, setWaitingAbsenceDays] = useState(0);

  // Delete modal (EMP)
  const [deleteId, setDeleteId] = useState(null);

  const [loadingAction, setLoadingAction] = useState(false);

  // Summary
  const [approvedHours, setApprovedHours] = useState(0);
  const [waitingHours, setWaitingHours] = useState(0);

  const reference = `${competence.year}-${String(competence.month).padStart(2, "0")}`;

  const fetchHours = useCallback(async () => {
    setLoading(true);
    try {
      // A listagem do back não pagina e corta em 50 sem limite: pede a competência inteira.
      // Paginar com os totais vindos do back é o item 1.2 do ROADMAP_BUGS.md.
      let query = `project_id=${projectId}&reference=${reference}&page=1&limit=9999`;
      if (userId) {
        query += `&user_id=${userId}`;
      }
      if (filterUser) {
        query += `&user_id=${filterUser}`;
      }
      const result = await projectHours.list(query);
      setFixedSummary(result?.fixed_mode_summary || null);
      if (result?.data) {
        const list = result.data.list || result.data || [];
        setHours(Array.isArray(list) ? list : []);

        // Calculate summaries
        let approved = 0;
        let waiting = 0;
        let absences = 0;
        let waitingAbsences = 0;
        const items = Array.isArray(list) ? list : [];
        const users = new Map();
        for (const item of items) {
          const records = (item.details && Array.isArray(item.details)) ? item.details : [item];
          for (const d of records) {
            const val = parseFloat(d.pure_total_hours || d.total_hours || 0);
            const isAbsence = d.record_type === "ABSENCE";
            if (d.status === "APPROVED") {
              if (isAbsence) absences += val;
              else approved += val;
            }
            if (d.status === "WAITING") {
              if (isAbsence) waitingAbsences += val;
              else waiting += val;
            }
            if (d.user && d.user.id && !users.has(d.user.id)) {
              users.set(d.user.id, d.user);
            }
          }
        }
        setApprovedHours(approved);
        setWaitingHours(waiting);
        setAbsenceDays(absences);
        setWaitingAbsenceDays(waitingAbsences);
        setAvailableUsers(Array.from(users.values()));

        // Abre em "Aguardando" quando há o que decidir na competência (a busca refaz com o filtro)
        if (!autoFilterApplied.current) {
          autoFilterApplied.current = true;
          if (waiting + waitingAbsences > 0) {
            setFilterStatus(STATUS_DATA.find((s) => s.value === "WAITING"));
          }
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar horas");
    }
    setLoading(false);
    setRowSelection({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, reference, filterUser]);

  useEffect(() => {
    if (projectId) fetchHours();
  }, [projectId, fetchHours]);

  // === Admin actions ===
  async function handleApproveHour(hourIds, approve, rejectionReason) {
    setLoadingAction(true);
    try {
      await projectHours.approve({ hours: hourIds, approve, ...(!approve && { rejection_reason: rejectionReason }) });
      toast.success(approve ? "Horas aprovadas!" : "Horas reprovadas!");
      setRejectIds(null);
      fetchHours();
    } catch (err) {
      toast.error("Erro ao atualizar horas");
    }
    setLoadingAction(false);
  }

  // === Employee actions ===
  function openNewHour() {
    setFormErrors({});
    setShowNewHour(true);
  }

  function closeNewHour() {
    if (loadingCreate) return;
    setShowNewHour(false);
  }

  async function handleCreateHour() {
    const isAbsence = newRecordType === "ABSENCE";
    const errors = {};

    if (!newHourDesc.trim()) errors.desc = "Preencha a descrição";
    const total = parseFloat(newHourTotal);
    if (!newHourTotal || !(total > 0)) errors.total = isAbsence ? "Informe os dias ausentes" : "Informe o total de horas";
    else if (isAbsence && !Number.isInteger(total)) errors.total = "Informe o número de dias inteiros ausentes";

    let referenceDate;
    if (markingModel === "DAILY") {
      if (!newHourDate) errors.date = "Informe a data";
      referenceDate = newHourDate;
    } else if (markingModel === "WEEKLY") {
      if (!newHourDate) errors.date = "Informe a data da segunda-feira da semana";
      else if (isoToDate(newHourDate).getDay() !== 1) errors.date = "A data deve ser uma segunda-feira";
      referenceDate = newHourDate;
    } else if (markingModel === "MONTHLY") {
      // Montar referenceDate como primeiro dia do mês selecionado
      referenceDate = `${newHourYear}-${String(newHourMonth).padStart(2, "0")}-01`;
    }

    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    // Validar período do contrato/projeto
    if (contract) {
      let periodCheck;
      if (markingModel === "WEEKLY") {
        const [yy, mm, dd] = referenceDate.split("-").map(Number);
        const weekStart = new Date(yy, mm - 1, dd);
        const weekEnd = new Date(yy, mm - 1, dd + 6);
        periodCheck = rangeIntersectsContractAndProjectPeriod(contract, weekStart, weekEnd);
      } else if (markingModel === "MONTHLY") {
        const monthStart = new Date(newHourYear, newHourMonth - 1, 1);
        const monthEnd = new Date(newHourYear, newHourMonth, 0);
        periodCheck = rangeIntersectsContractAndProjectPeriod(contract, monthStart, monthEnd);
      } else {
        periodCheck = isWithinContractAndProjectPeriod(contract, referenceDate);
      }
      if (!periodCheck.valid) {
        toast.error(periodCheck.reason);
        return;
      }
    }

    setLoadingCreate(true);
    try {
      const result = await projectHours.create({
        project_id: projectId,
        marking_type: markingModel,
        reference_date: referenceDate,
        total_hours: parseFloat(newHourTotal),
        description: newHourDesc,
        record_type: newRecordType,
      });
      if (result && !result.error) {
        toast.success("Hora apontada com sucesso!");
        setShowNewHour(false);
        setNewHourDate("");
        setNewHourTotal("");
        setNewHourDesc("");
        fetchHours();
      }
      // Erro: BaseService.sendError já disparou o toast com a mensagem do backend.
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Erro ao apontar hora");
    }
    setLoadingCreate(false);
  }

  async function handleDeleteHour() {
    setLoadingAction(true);
    try {
      const result = await projectHours.delete(deleteId);
      // Back responde 204 sem corpo: resposta vazia também é sucesso.
      if (!result?.error) {
        toast.success("Apontamento removido!");
        setDeleteId(null);
        fetchHours();
      }
      // Erro: BaseService.sendError já disparou o toast com a mensagem do backend.
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Erro ao remover apontamento");
    }
    setLoadingAction(false);
  }

  function formatReferenceLabel(item) {
    const refDate = item.reference_date;
    const mType = item.marking_type || markingModel;
    if (!refDate) return "—";
    // Parse date string (could be "YYYY-MM-DD" or ISO)
    const dateStr = typeof refDate === "string" ? refDate.split("T")[0] : new Date(refDate).toISOString().split("T")[0];
    const [year, month, day] = dateStr.split("-").map(Number);
    if (mType === "WEEKLY") {
      return `Semana de ${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
    }
    if (mType === "MONTHLY") {
      return `${MONTH_NAMES[month - 1]}/${year}`;
    }
    // DAILY ou legado
    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  }

  function referenceHeader() {
    if (markingModel === "WEEKLY") return "Semana";
    if (markingModel === "MONTHLY") return "Mês/ano";
    return "Data";
  }

  function hoursValue(record) {
    const value = record.pure_total_hours ?? record.total_hours;
    return (
      <Inline>
        {record.record_type === "ABSENCE" ? (
          <StatePill $text="Ausência" $variant="orange" $size="small" />
        ) : trackingMode !== "HOURS_ONLY" ? (
          <StatePill $text="Horas" $variant="gray" $size="small" />
        ) : null}
        <Text
          $text={record.record_type === "ABSENCE" ? days(record.pure_total_hours || record.total_hours) : hrs(value)}
          $variant="paragraph_small_bold"
          $color="N900"
        />
      </Inline>
    );
  }

  // O back agrupa por status + prestador para quem lê o projeto (inclusive o próprio
  // prestador). Na tela vira uma linha por lançamento: cada um com a sua aprovação
  // e a sua exclusão (antes, excluir um grupo apagava só o primeiro registro).
  // O status filtra na tela: a competência vem inteira, e os totais valem para ela toda
  const rows = hours
    .flatMap((item) =>
      (item.details && item.details.length ? item.details : [item]).map((d) => ({
        ...d,
        createdBy: d.created_by_name || d.user?.name || item.created_by_name || "—",
      }))
    )
    .filter((r) => !filterStatus.value || r.status === filterStatus.value);

  const recordsById = new Map(rows.map((r) => [r.id, r]));
  const selectedIds = Object.keys(rowSelection)
    .filter((id) => rowSelection[id])
    .map(Number)
    .filter((id) => recordsById.has(id));

  function sumOf(ids) {
    let h = 0;
    let a = 0;
    ids.forEach((id) => {
      const r = recordsById.get(id);
      if (!r) return;
      const val = parseFloat(r.pure_total_hours ?? r.total_hours) || 0;
      if (r.record_type === "ABSENCE") a += val;
      else h += val;
    });
    return { hours: h, absences: a };
  }
  function sumLabel(ids) {
    const s = sumOf(ids);
    const parts = [];
    if (s.hours || !s.absences) parts.push(hrs(s.hours));
    if (s.absences) parts.push(days(s.absences));
    return parts.join(" e ");
  }

  const referenceColumn = { id: "reference", header: referenceHeader(), size: 150, cell: ({ row }) => formatReferenceLabel(row.original) };
  const hoursColumn = { id: "hours", header: "Horas", size: 140, cell: ({ row }) => hoursValue(row.original) };
  const descriptionColumn = {
    id: "description",
    header: "Descrição",
    size: 320,
    maxSize: 420,
    cell: ({ row }) => (
      <Stack $gap="2px">
        <span>{row.original.description || "—"}</span>
        {row.original.status === "REJECTED" && row.original.rejection_reason ? (
          <Text
            $text="Motivo: %s"
            $replaces={[escapeHtml(row.original.rejection_reason)]}
            $variant="caption_medium"
            $color="N500"
          />
        ) : null}
      </Stack>
    ),
  };
  const statusColumn = {
    id: "status",
    header: "Status",
    size: 120,
    cell: ({ row }) => <DomainStatus domain="horas" value={HOUR_STATUS[row.original.status] || row.original.status} />,
  };

  const adminColumns = [
    ...(canDecide ? [createSelectionColumn()] : []),
    referenceColumn,
    hoursColumn,
    descriptionColumn,
    { id: "createdBy", header: "Lançado por", size: 180, cell: ({ row }) => row.original.createdBy },
    statusColumn,
  ];
  if (canDecide) {
    adminColumns.push({
      id: "actions",
      size: 220,
      maxSize: 220,
      align: "right",
      cell: ({ row }) =>
        row.original.status === "WAITING" ? (
          <Inline>
            <Button
              $text="Aprovar"
              $variant="outline"
              $size="small"
              $noWidth
              $isDisabled={loadingAction}
              $action={() => handleApproveHour([row.original.id], true)}
            />
            <Button
              $text="Reprovar"
              $variant="outline_danger"
              $size="small"
              $noWidth
              $isDisabled={loadingAction}
              $action={() => setRejectIds([row.original.id])}
            />
          </Inline>
        ) : null,
    });
  }

  const employeeColumns = [
    referenceColumn,
    hoursColumn,
    descriptionColumn,
    statusColumn,
    {
      id: "actions",
      size: 120,
      maxSize: 120,
      align: "right",
      cell: ({ row }) =>
        row.original.status === "WAITING" ? (
          <Button $text="Excluir" $variant="outline_danger" $size="small" $noWidth $action={() => setDeleteId(row.original.id)} />
        ) : null,
    },
  ];

  const filtered = !!(filterStatus.value || filterUser);

  // Totais: o rodapé do antigo "Saldo do período" virou itens próprios
  const totals = [];
  if (isFixedMode && fixedSummary) totals.push({ name: "Jornada esperada", value: hrs(fixedSummary.expected_hours), color: "N900" });
  if (trackingMode !== "ABSENCES_ONLY") totals.push({ name: "Aprovadas", value: hrs(approvedHours), color: "N900" });
  totals.push({
    name: "Aguardando",
    color: "N900",
    value:
      trackingMode === "ABSENCES_ONLY"
        ? days(waitingAbsenceDays)
        : trackingMode === "HOURS_AND_ABSENCES" && waitingAbsenceDays > 0
        ? waitingHours > 0
          ? `${hrs(waitingHours)} / ${days(waitingAbsenceDays)}`
          : days(waitingAbsenceDays)
        : hrs(waitingHours),
  });
  // Sem contrato (detalhe do projeto, que mistura contratos), as ausências aparecem quando houver
  if (trackingMode === "HOURS_AND_ABSENCES" || trackingMode === "ABSENCES_ONLY" || (!hourTrackingMode && absenceDays > 0)) {
    totals.push({ name: "Ausências aprovadas", value: days(absenceDays), color: "N900" });
  }
  if (isFixedMode && fixedSummary) {
    const balance = fixedSummary.balance_hours;
    const balanceColor = balance < 0 ? "E400" : balance > 0 ? "P800" : "N900";
    // "Horas no pagamento" reconcilia o saldo: as aprovadas até o fechamento mais o
    // adiantamento, menos o que já foi cobrado antes (a mesma conta do cronograma).
    totals.push(
      { name: "Horas no pagamento", value: hrs(fixedSummary.paid_hours), color: "N900" },
      { name: "Saldo do período", value: `${balance > 0 ? "+" : ""}${hrs(balance)}`, color: balanceColor }
    );
    if (balance !== 0 && contract?.hourly_rate) {
      totals.push({ name: "Saldo em valor", value: `≈ ${formatBRL(balance * parseFloat(contract.hourly_rate))}`, color: balanceColor });
    }
  }

  const selectedSum = sumOf(selectedIds);
  const selectionStats = [];
  if (selectedSum.hours || !selectedSum.absences) selectionStats.push({ label: "Horas", value: hrs(selectedSum.hours) });
  if (selectedSum.absences) selectionStats.push({ label: "Ausências", value: days(selectedSum.absences) });

  const rejectName = rejectIds && rejectIds.length ? recordsById.get(rejectIds[0])?.createdBy : null;
  const competenceLabel = formatCompetencia(competence, true);

  function clearFilters() {
    setFilterStatus(STATUS_DATA[0]);
    setFilterUser("");
  }

  const noResults = filtered ? (
    <NoResults notResultText="Nenhum apontamento com estes filtros" includeText="Limpar filtros" includeAction={clearFilters} />
  ) : (
    <NoResults notResultText="Nenhum apontamento de hora encontrado para este período" />
  );

  const competencePicker = (
    <CompetenceField>
      <DatePicker
        $idForm={isAdmin ? "hours-competence" : "my-hours-competence"}
        $label="Competência"
        $variant="default"
        $isCompetence
        // O campo fica à direita no ADM (depois dos filtros) e à esquerda no prestador
        $popperPlacement={isAdmin ? "bottom-end" : "bottom-start"}
        $date={new Date(competence.year, competence.month - 1, 1)}
        $setDate={(date) => {
          if (date) setCompetence({ month: date.getMonth() + 1, year: date.getFullYear() });
        }}
      />
    </CompetenceField>
  );

  const userOptions = availableUsers.map((u) => ({ value: String(u.id), label: u.name }));
  const newHourDateHelper = [
    markingModel === "WEEKLY" ? "Selecione a segunda-feira da semana trabalhada." : null,
    closingDay && newHourDate ? `Entra na competência ${competenceOfDate(newHourDate, closingDay)} (fechamento dia ${closingDay}).` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <ContentPage>
      {/* Filtros */}
      <Row $justifyContent="space-between" $alignItems="flex-end" $gap={16}>
        {isAdmin ? (
          <>
            <Filters>
              <SelectorPageStatus
                options={STATUS_DATA}
                selected={filterStatus}
                onChange={(option) => {
                  setFilterStatus(option);
                  setRowSelection({});
                }}
              />
              {!userId && userOptions.length > 0 ? (
                <PersonField>
                  <Select
                    $idForm="hours-person"
                    $label="Prestador"
                    $options={userOptions}
                    $isClearable
                    value={userOptions.find((o) => o.value === String(filterUser)) || null}
                    onChange={(option) => setFilterUser(option ? option.value : "")}
                  />
                </PersonField>
              ) : null}
            </Filters>
            {competencePicker}
          </>
        ) : (
          <>
            {competencePicker}
            <div>
              <Button
                $text={trackingMode === "ABSENCES_ONLY" ? "Registrar ausência" : "Apontar horas"}
                $variant="primary"
                $noWidth
                $action={openNewHour}
              />
            </div>
          </>
        )}
      </Row>

      {/* Resumo */}
      {loading ? <Skeleton $height={56} /> : <Totalizers totals={totals} />}

      {isFixedMode && !isAdmin ? (
        <Notice
          $variant="blue"
          $text="Você marca as horas trabalhadas normalmente. Não existe registro de falta — o que vale é o total do mês. Esqueceu de marcar um dia? Pode lançar depois. Faltou um dia e compensou no outro? O mês fecha sem desconto."
        />
      ) : null}

      {/* Tabela */}
      {isAdmin ? (
        <Stack>
          <BasicTable
            data={rows}
            columns={adminColumns}
            getRowId={(row) => String(row.id)}
            isLoading={loading}
            noResults={noResults}
            enableRowSelection={canDecide ? (row) => row.original.status === "WAITING" : false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
          />
          {canDecide ? (
            <SelectionBar $count={selectedIds.length} $stats={selectionStats} $onClear={() => setRowSelection({})}>
              <Button $text="Reprovar" $variant="outline_danger" $noWidth $isDisabled={loadingAction} $action={() => setRejectIds(selectedIds)} />
              <Button
                $text="Aprovar"
                $variant="primary"
                $noWidth
                $isLoading={loadingAction && !rejectIds}
                $isDisabled={loadingAction}
                $action={() => handleApproveHour(selectedIds, true)}
              />
            </SelectionBar>
          ) : null}
        </Stack>
      ) : (
        <BasicTable data={rows} columns={employeeColumns} getRowId={(row) => String(row.id)} isLoading={loading} noResults={noResults} />
      )}

      {/* Modal: Reprovar com motivo (Admin) */}
      {rejectIds && (
        <RejectHoursModal
          title={rejectIds.length > 1 ? `Reprovar ${rejectIds.length} lançamentos` : "Reprovar lançamento"}
          summary={{
            text: "<strong>%s</strong>%s em %s. %s",
            replaces: [
              sumLabel(rejectIds),
              rejectName && rejectName !== "—" ? ` de ${escapeHtml(rejectName)}` : "",
              competenceLabel,
              rejectIds.length > 1 ? "Os lançamentos voltam para o prestador como reprovados." : "O lançamento volta para o prestador como reprovado.",
            ],
          }}
          loading={loadingAction}
          onConfirm={(reason) => handleApproveHour(rejectIds, false, reason)}
          onClose={() => setRejectIds(null)}
        />
      )}

      {/* Modal: Nova Hora (EMP) */}
      {showNewHour && (
        <ModalForm
          $showModal={showNewHour}
          $setShowModal={closeNewHour}
          $noClose={loadingCreate}
          $isLoading={loadingCreate}
          $size="medium"
          $title={trackingMode === "ABSENCES_ONLY" || newRecordType === "ABSENCE" ? "Registrar ausência" : "Apontar horas"}
          $contentBackground="N0"
          $actions={[
            { text: "Cancelar", action: closeNewHour, variant: "outline", isDisabled: loadingCreate },
            { text: newRecordType === "ABSENCE" ? "Registrar" : "Apontar", action: handleCreateHour, variant: "primary", isDisabled: loadingCreate },
          ]}
        >
          <Stack $gap="20px">
            {/* Tipo de registro para HOURS_AND_ABSENCES */}
            {trackingMode === "HOURS_AND_ABSENCES" ? (
              <InputRadio
                name="record-type"
                $legend="Tipo de registro"
                $options={[
                  { value: "HOURS", label: "Horas trabalhadas" },
                  { value: "ABSENCE", label: "Ausência" },
                ]}
                value={newRecordType}
                onChange={(e) => setNewRecordType(e.target.value)}
              />
            ) : null}

            {/* Campo de referência conforme o modelo de marcação */}
            {markingModel === "DAILY" || markingModel === "WEEKLY" ? (
              <Field>
                <DatePicker
                  $idForm="new-hour-date"
                  $centralize
                  $label={markingModel === "WEEKLY" ? "Segunda-feira da semana" : "Data"}
                  $variant="default"
                  $isRequired
                  $errors={formErrors.date}
                  $date={isoToDate(newHourDate)}
                  $setDate={(date) => {
                    setNewHourDate(localIsoDate(date) || "");
                    if (formErrors.date) setFormErrors((prev) => ({ ...prev, date: "" }));
                  }}
                />
                {newHourDateHelper ? <Text $text={newHourDateHelper} $variant="caption_medium" $color="N400" /> : null}
              </Field>
            ) : null}
            {markingModel === "MONTHLY" ? (
              <DatePicker
                $idForm="new-hour-month"
                $centralize
                $label="Mês"
                $variant="default"
                $isCompetence
                $isRequired
                $date={new Date(newHourYear, newHourMonth - 1, 1)}
                $setDate={(date) => {
                  if (!date) return;
                  setNewHourMonth(date.getMonth() + 1);
                  setNewHourYear(date.getFullYear());
                }}
              />
            ) : null}

            <Field>
              <InputText
                $idForm="new-hour-total"
                $label={newRecordType === "ABSENCE" ? "Dias ausentes" : "Total de horas"}
                $variant="default"
                $isRequired
                $errors={formErrors.total}
                type="number"
                inputMode="decimal"
                min={newRecordType === "ABSENCE" ? "1" : "0.5"}
                step={newRecordType === "ABSENCE" ? "1" : "0.5"}
                value={newHourTotal}
                onChange={(e) => {
                  setNewHourTotal(e.target.value);
                  if (formErrors.total) setFormErrors((prev) => ({ ...prev, total: "" }));
                }}
                placeholder={newRecordType === "ABSENCE" ? "Ex.: 1" : "Ex.: 8"}
              />
              {newRecordType === "ABSENCE" ? (
                <Text $text="Informe o número de dias inteiros ausentes." $variant="caption_medium" $color="N400" />
              ) : null}
            </Field>
            <InputTextarea
              $idForm="new-hour-description"
              $label="Descrição"
              $variant="default"
              $isRequired
              $errors={formErrors.desc}
              rows={3}
              value={newHourDesc}
              onChange={(e) => {
                setNewHourDesc(e.target.value);
                if (formErrors.desc) setFormErrors((prev) => ({ ...prev, desc: "" }));
              }}
              placeholder={newRecordType === "ABSENCE" ? "Motivo da ausência…" : "Descreva a atividade realizada…"}
            />
          </Stack>
        </ModalForm>
      )}

      {/* Modal: Confirmar Exclusão (EMP) */}
      {deleteId && (
        <ModalDefault
          $showModal={!!deleteId}
          $setShowModal={() => !loadingAction && setDeleteId(null)}
          $noClose={loadingAction}
          $size="medium"
          $title="Excluir apontamento?"
          $contentBackground="N0"
          $footer={
            <>
              <Button $text="Cancelar" $variant="outline" $noWidth $isDisabled={loadingAction} $action={() => setDeleteId(null)} />
              <Button $text="Excluir" $variant="danger" $noWidth $isLoading={loadingAction} $action={handleDeleteHour} />
            </>
          }
        >
          <Text
            $text="O lançamento sai da competência. Você pode apontar de novo depois."
            $variant="paragraph_small_medium"
            $color="N700"
          />
        </ModalDefault>
      )}
    </ContentPage>
  );
}
